/**
 * SelectionTip.tsx — Annotation creation UI
 *
 * Renders inline when the user selects text in the PDF.
 * Two modes:
 *   1. "pick" — four buttons for annotation type
 *   2. "note" — textarea for note text
 *
 * Key design: we capture selection data (text, page, position) eagerly
 * inside the tip — before the browser clears the selection on click —
 * and pass the captured data up through onConfirm. This avoids the race
 * condition where getCurrentSelection() returns null after a button click.
 */
import React, { useState, useLayoutEffect } from "react";
import { usePdfHighlighterContext } from "react-pdf-highlighter-extended";
import type { AnnotationType } from "./types";
import { TYPE_COLORS } from "./types";

/**
 * Data passed from the tip to the parent when the user confirms.
 * Contains everything needed to create the annotation without
 * needing to re-read the (possibly cleared) browser selection.
 */
export interface SelectionData {
  selectedText: string;
  pageNumber: number;
  position: any; // ScaledPosition from the library
}

interface SelectionTipProps {
  onConfirm: (type: AnnotationType, selectionData: SelectionData, noteText?: string) => void;
}

export const SelectionTip: React.FC<SelectionTipProps> = ({ onConfirm }) => {
  const [mode, setMode] = useState<"pick" | "note">("pick");
  const [noteType, setNoteType] = useState<AnnotationType>("note");
  const [noteText, setNoteText] = useState("");
  const [capturedSelection, setCapturedSelection] = useState<SelectionData | null>(null);

  const { getCurrentSelection, updateTipPosition } =
    usePdfHighlighterContext();

  // Reposition the tip when switching to note mode (it gets taller)
  useLayoutEffect(() => {
    updateTipPosition!();
  }, [mode, updateTipPosition]);

  /**
   * Capture the current selection data right now, before the browser
   * clears it. Returns null if no valid selection.
   */
  const captureSelection = (): SelectionData | null => {
    // If we already captured it (e.g. switching to note mode), reuse
    if (capturedSelection) return capturedSelection;

    const sel = getCurrentSelection();
    if (!sel) return null;

    const ghost = sel.makeGhostHighlight();
    if (!ghost) return null;

    const data: SelectionData = {
      selectedText: ghost.content?.text || "",
      pageNumber: ghost.position?.pageNumber || 1,
      position: ghost.position,
    };

    setCapturedSelection(data);
    return data;
  };

  const types: AnnotationType[] = [
    "highlight",
    "note",
  ];

  if (mode === "pick") {
    return (
      <div
        style={{
          display: "flex",
          gap: "2px",
          background: "#1e1e1e",
          border: "1px solid #333",
          borderRadius: "8px",
          padding: "4px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
        }}
      >
        {types.map((type) => (
          <button
            key={type}
            onClick={() => {
              const selData = captureSelection();
              if (!selData) return;

              if (type === "highlight") {
                onConfirm(type, selData);
              } else {
                setNoteType(type);
                setMode("note");
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 10px",
              border: "none",
              borderRadius: "6px",
              background: "transparent",
              color: "#ccc",
              fontSize: "12px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "background 0.1s",
            }}
            onMouseOver={(e) => {
              (e.currentTarget as HTMLElement).style.background = "#2a2a2a";
              (e.currentTarget as HTMLElement).style.color = "#fff";
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLElement).style.background = "transparent";
              (e.currentTarget as HTMLElement).style.color = "#ccc";
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: TYPE_COLORS[type].solid,
                flexShrink: 0,
              }}
            />
            {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        ))}
      </div>
    );
  }

  // ── Note input mode ──────────────────────────────────────────
  const labels: Record<string, string> = {
    note: "Add Note",
  };

  const placeholders: Record<string, string> = {
    note: "Your note...",
  };

  return (
    <div
      style={{
        width: "320px",
        background: "#171717",
        border: "1px solid #404040",
        borderRadius: "12px",
        padding: "16px",
        boxShadow: "0 12px 32px rgba(0,0,0,0.6)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "10px",
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: "13px",
            fontWeight: 600,
            color: "#fff",
          }}
        >
          {labels[noteType] || "Add Note"}
        </h3>
        <button
          onClick={() => {
            setMode("pick");
            setNoteText("");
            setCapturedSelection(null);
          }}
          style={{
            background: "none",
            border: "none",
            color: "#888",
            cursor: "pointer",
            fontSize: "16px",
            lineHeight: 1,
            padding: "2px",
          }}
        >
          ✕
        </button>
      </div>

      <textarea
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        placeholder={placeholders[noteType] || "Your note..."}
        rows={3}
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            const selData = capturedSelection || captureSelection();
            if (selData) {
              onConfirm(noteType, selData, noteText.trim() || undefined);
            }
            setMode("pick");
            setNoteText("");
            setCapturedSelection(null);
          }
          if (e.key === "Escape") {
            setMode("pick");
            setNoteText("");
            setCapturedSelection(null);
          }
        }}
        style={{
          width: "100%",
          boxSizing: "border-box",
          background: "#262626",
          border: "1px solid #404040",
          borderRadius: "6px",
          padding: "8px 10px",
          fontSize: "13px",
          color: "#e5e5e5",
          resize: "none",
          outline: "none",
          fontFamily: "inherit",
          lineHeight: "1.5",
        }}
      />

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "8px",
          marginTop: "10px",
        }}
      >
        <button
          onClick={() => {
            setMode("pick");
            setNoteText("");
            setCapturedSelection(null);
          }}
          style={{
            padding: "5px 12px",
            border: "none",
            borderRadius: "6px",
            background: "transparent",
            color: "#888",
            fontSize: "12px",
            cursor: "pointer",
          }}
        >
          Cancel
        </button>
        <button
          onClick={() => {
            const selData = capturedSelection || captureSelection();
            if (selData) {
              onConfirm(noteType, selData, noteText.trim() || undefined);
            }
            setMode("pick");
            setNoteText("");
            setCapturedSelection(null);
          }}
          style={{
            padding: "5px 14px",
            border: "none",
            borderRadius: "6px",
            background: "#dc2626",
            color: "#fff",
            fontSize: "12px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
};
